import { Check, Lock } from 'lucide-react'
import { cn } from '@/lib/cn'
import { teamColor, teamLabel } from '@/lib/teamColors'

type Side = 'away' | 'home' | null

/* A game is a 50/50 split. The picked side takes a ~10% green tint, a 2px green
   border and a check — not a saturated fill. Fifteen solid green blocks on one
   screen flattens the page into a wall of color and the team names stop being
   what you read; the emphasis belongs on the team, with green as the marker
   that you chose it.

   `away` and `home` are canonical abbreviations from the games table, not
   display strings — they key into teamColors for the color bar and the two-line
   city/nickname label. The nickname carries the row because four teams share a
   city with a division rival.

   Green still means *picked*, never *correct*. Results are the Board's job.

   Each half carries a 4px bar in its team's primary color on the row's outer
   edge — left for away, right for home — which is why the bars are siblings of
   the buttons rather than children: a child would sit inside the picked
   border's 2px inset and stop lining up with the row edge.

   Locked rows drop to 55% and lose their tap targets entirely rather than
   showing a disabled cursor nobody sees on a phone.

   The LOCK toggle sits outside the bordered row rather than inside it, so the
   team color bars stay on the row's own outer edges. It is inert until a team is
   picked on that row — a Lock is a wager on a pick, so there is nothing to
   double until one exists. */
export default function PickRow({
  away,
  home,
  picked,
  isLock,
  locked,
  onPick,
  lockEnabled,
  onToggleLock,
  lockPending,
}: {
  away: string
  home: string
  picked: Side
  isLock?: boolean
  locked?: boolean
  onPick?: (side: Exclude<Side, null>) => void
  lockEnabled?: boolean
  onToggleLock?: () => void
  lockPending?: boolean
}) {
  const half = (side: Exclude<Side, null>, team: string) => {
    const on = picked === side
    const { city, nickname } = teamLabel(team)
    return (
      <button
        type="button"
        disabled={locked}
        aria-pressed={on}
        onClick={() => onPick?.(side)}
        className={cn(
          'min-w-0 flex-1 border-2 px-3 py-3 text-left transition-colors',
          on
            ? 'border-field bg-field-tint'
            : 'border-transparent bg-card hover:bg-field-tint/50'
        )}
      >
        <span
          className={cn(
            'font-mono text-eyebrow tracking-[0.14em] uppercase',
            on ? 'text-mark' : 'text-ink-dim'
          )}
        >
          {side}
        </span>
        {/* Dropped entirely rather than rendered empty when the abbreviation is
            unknown — an empty line would leave the nickname sitting at a
            different height from every other row in the slate. */}
        {city && (
          <span className="text-ink-dim mt-1.5 block text-[0.75rem] leading-none font-normal">
            {city}
          </span>
        )}
        <span className="text-ink mt-1 flex items-baseline gap-1.5 text-[1.25rem] leading-tight font-bold tracking-[-0.01em]">
          {nickname}
          {on && <Check className="text-field size-4 shrink-0 stroke-[3]" aria-hidden />}
        </span>
      </button>
    )
  }

  const bar = (team: string) => (
    <span
      aria-hidden
      className="w-1 shrink-0"
      style={{ backgroundColor: teamColor(team) }}
    />
  )

  return (
    <div className={cn('mb-2.5', locked && 'opacity-55')}>
      <div className="flex items-stretch gap-2">
        <div
          className={cn(
            'rounded-row flex min-w-0 flex-1 items-stretch overflow-hidden border',
            isLock ? 'border-mark border-2' : 'border-rule'
          )}
        >
          {bar(away)}
          {half('away', away)}
          <div className="bg-rule w-px" aria-hidden />
          {half('home', home)}
          {bar(home)}
        </div>

        {lockEnabled && (
          <button
            type="button"
            onClick={onToggleLock}
            disabled={locked || !picked || lockPending}
            aria-pressed={Boolean(isLock)}
            aria-label={
              !picked
                ? 'Pick a team before setting your Lock of the Week'
                : isLock
                  ? 'Remove Lock of the Week from this game'
                  : 'Make this game your Lock of the Week'
            }
            className={cn(
              'rounded-row flex w-11 shrink-0 flex-col items-center justify-center gap-1 border-2 transition-colors',
              isLock
                ? 'border-mark bg-mark text-white'
                : 'border-rule bg-card text-ink-dim',
              picked && !isLock && !locked && 'hover:border-mark hover:text-mark',
              !picked && 'opacity-40'
            )}
          >
            <Lock className="size-3.5" aria-hidden />
            <span className="font-mono text-[0.625rem] tracking-[0.08em] uppercase">Lock</span>
          </button>
        )}
      </div>
      {isLock && (
        <p className="text-mark mt-1 font-mono text-eyebrow tracking-[0.16em] uppercase">
          Lock of the week · 2 pts
        </p>
      )}
      {locked && !isLock && (
        <p className="text-flag mt-1 font-mono text-eyebrow tracking-[0.16em] uppercase">
          Kicked off · closed
        </p>
      )}
    </div>
  )
}
